import {
	createFileRoute,
	useCanGoBack,
	useNavigate,
	useRouteContext,
	useRouter,
} from "@tanstack/react-router";
import { Suspense, useMemo } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { Heading } from "@/components/heading";
import { Skeleton } from "@/components/ui/skeleton";
import {
	EmailTemplateForm,
	type EmailTemplateFormSchemaValues,
} from "@/forms/email-template-form";
import type { templates_idQuery } from "./__generated__/templates_idQuery.graphql";
import type { templates_idUpdateMutation } from "./__generated__/templates_idUpdateMutation.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(standalone)/templates/$id",
)({
	component: RouteComponent,
});

function TemplateSkeleton() {
	return (
		<div className="h-screen flex flex-col">
			<header className="flex items-center justify-between h-14 px-4 border-b">
				<div className="flex items-center gap-2">
					<Skeleton className="h-4 w-20" />
					<span className="text-muted-foreground">/</span>
					<Skeleton className="h-4 w-32" />
				</div>
				<div className="flex items-center gap-2">
					<Skeleton className="h-8 w-20" />
					<Skeleton className="h-8 w-20" />
				</div>
			</header>
			<div className="flex-1 flex">
				<div className="w-80 border-r p-4 space-y-4">
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
				</div>
				<div className="flex-1 p-4">
					<Skeleton className="h-full w-full" />
				</div>
			</div>
		</div>
	);
}

function TemplateContent() {
	const router = useRouter();
	const canGoBack = useCanGoBack();
	const navigate = useNavigate({ from: Route.fullPath });
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { id } = Route.useParams();

	const data = useLazyLoadQuery<templates_idQuery>(
		graphql`
			query templates_idQuery($id: ID!, $clubSlug: String!) {
				node(id: $id) {
					... on Template {
						id
						rawID
						title
						subject
						content
						from
						fromName
						replyTo
					}
				}
				club(slug: $clubSlug) {
					emailDomains {
						domain
						enabled
						isDefault
					}
				}
			}
		`,
		{ id, clubSlug: club.slug },
	);

	const [updateTemplate, isPending] =
		useMutation<templates_idUpdateMutation>(graphql`
			mutation templates_idUpdateMutation($payload: UpdateTemplateInput!) {
				updateTemplate(updateTemplateInput: $payload) {
					id
					title
					subject
					content
					from
					fromName
					replyTo
					updatedAt
				}
			}
		`);

	const template = data.node;

	const emailDomains = useMemo(
		() =>
			data.club.emailDomains.map((d) => ({
				domain: d.domain,
				enabled: d.enabled,
				isDefault: d.isDefault,
			})),
		[data.club.emailDomains],
	);

	const defaultDomain = useMemo(() => {
		const defaultEmailDomain = emailDomains.find(
			(d) => d.isDefault && d.enabled,
		);
		return (
			defaultEmailDomain?.domain ||
			emailDomains.find((d) => d.enabled)?.domain ||
			"scarf.club"
		);
	}, [emailDomains]);

	if (!template?.id) {
		return (
			<div className="h-screen flex items-center justify-center">
				<div className="p-6 max-w-md text-center">
					<Heading>Template não encontrado</Heading>
					<p className="text-muted-foreground mt-2">
						O template que você está procurando não existe ou foi removido.
					</p>
				</div>
			</div>
		);
	}

	const onSubmit = (value: EmailTemplateFormSchemaValues) => {
		return updateTemplate({
			variables: {
				payload: {
					id: template.rawID as string,
					title: value.title,
					subject: value.subject,
					content: JSON.stringify(value.content),
					from: value.from,
					fromName: value.fromName || undefined,
					replyTo: value.replyTo || undefined,
				},
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Template atualizado com sucesso!");

				if (canGoBack) {
					router.history.back();
				} else {
					navigate({ to: "/c/$club/automacoes/templates-de-email" });
				}
			},
			onError: (error) => {
				console.error("Error updating template:", error);
				toast.error(
					"Ocorreu um erro ao atualizar o template. Verifique os dados e tente novamente.",
				);
			},
		});
	};

	const defaultValues: EmailTemplateFormSchemaValues = {
		title: template.title || "",
		subject: template.subject || "",
		from:
			template.from ||
			(defaultDomain?.includes("@")
				? defaultDomain
				: `noreply@${defaultDomain}`),
		fromName: template.fromName || "",
		replyTo: template.replyTo || "",
		content:
			typeof template.content === "string"
				? JSON.parse(template.content)
				: template.content || { type: "doc", content: [] },
	};

	return (
		<div className="h-screen">
			<EmailTemplateForm
				onSubmit={onSubmit}
				defaultValues={defaultValues}
				isSubmitting={isPending}
				isEditing
				emailDomains={emailDomains}
			/>
		</div>
	);
}

function RouteComponent() {
	return (
		<Suspense fallback={<TemplateSkeleton />}>
			<TemplateContent />
		</Suspense>
	);
}
